var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import { TabList } from "@mui/lab";
import { useEffect, useRef, useState } from "react";
var ScrollableTabList = function (_a) {
    var children = _a.children, onChange = _a.onChange;
    var tabListRef = useRef(null);
    var _b = useState(false), canScrollLeft = _b[0], setCanScrollLeft = _b[1];
    var _c = useState(false), canScrollRight = _c[0], setCanScrollRight = _c[1];
    var getScroller = function () {
        var _a;
        return (_a = tabListRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.MuiTabs-scroller');
    };
    var updateScrollState = function () {
        var scroller = getScroller();
        if (!scroller) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        var maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
        setCanScrollLeft(scroller.scrollLeft > 1);
        setCanScrollRight(maxScrollLeft - scroller.scrollLeft > 1);
    };
    var scrollByDirection = function (direction) {
        var scroller = getScroller();
        if (!scroller)
            return;
        var scrollAmount = Math.max(scroller.clientWidth * 0.7, 160);
        scroller.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };
    useEffect(function () {
        var cleanup;
        var frameId = window.requestAnimationFrame(function () {
            var scroller = getScroller();
            updateScrollState();
            if (!scroller)
                return;
            var handleScroll = function () { return updateScrollState(); };
            var handleResize = function () { return updateScrollState(); };
            var resizeObserver = new ResizeObserver(function () { return updateScrollState(); });
            var tabList = scroller.querySelector('[role="tablist"]');
            scroller.addEventListener('scroll', handleScroll, { passive: true });
            window.addEventListener('resize', handleResize);
            resizeObserver.observe(scroller);
            if (tabList instanceof Element) {
                resizeObserver.observe(tabList);
            }
            cleanup = function () {
                scroller.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleResize);
                resizeObserver.disconnect();
            };
        });
        return function () {
            window.cancelAnimationFrame(frameId);
            cleanup === null || cleanup === void 0 ? void 0 : cleanup();
        };
    }, [children]);
    return (_jsxs(Box, __assign({ sx: {
            borderBottom: 1,
            borderColor: 'divider',
            position: 'relative',
            bgcolor: 'background.paper',
            pt: 0.75,
            mt: -0.75,
            overflow: 'visible'
        } }, { children: [_jsx(TabList, __assign({ ref: tabListRef, onChange: onChange, variant: "scrollable", scrollButtons: false, "aria-label": "scrollable tabs", sx: {
                    minHeight: 48,
                    '& .MuiTabs-scroller': {
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    },
                    '& .MuiTabs-scroller::-webkit-scrollbar': {
                        display: 'none'
                    }
                } }, { children: children })), canScrollLeft && (_jsx(Box, __assign({ sx: {
                    position: 'absolute',
                    left: 0,
                    top: 5,
                    bottom: 6,
                    width: { xs: 56, sm: 72 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    pl: 0.5,
                    pt: 0.5,
                    background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 150%)',
                    pointerEvents: 'none',
                    zIndex: 1
                } }, { children: _jsx(IconButton, __assign({ size: "small", onClick: function () { return scrollByDirection('left'); }, "aria-label": "Scroll tabs left", sx: {
                        pointerEvents: 'auto',
                        bgcolor: 'rgba(120, 120, 120, 0.24)',
                        color: 'text.primary',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                        '&:hover': {
                            bgcolor: 'rgba(120, 120, 120, 0.32)'
                        }
                    } }, { children: _jsx(ChevronLeftIcon, {}) })) }))), canScrollRight && (_jsx(Box, __assign({ sx: {
                    position: 'absolute',
                    right: 0,
                    top: 5,
                    bottom: 6,
                    width: { xs: 56, sm: 72 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    pr: 0.5,
                    pt: 0.5,
                    background: 'linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 150%)',
                    pointerEvents: 'none',
                    zIndex: 1
                } }, { children: _jsx(IconButton, __assign({ size: "small", onClick: function () { return scrollByDirection('right'); }, "aria-label": "Scroll tabs right", sx: {
                        pointerEvents: 'auto',
                        bgcolor: 'rgba(120, 120, 120, 0.24)',
                        color: 'text.primary',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                        '&:hover': {
                            bgcolor: 'rgba(120, 120, 120, 0.32)'
                        }
                    } }, { children: _jsx(ChevronRightIcon, {}) })) })))] })));
};
export default ScrollableTabList;
